import { cn } from '@/lib/utils';

export type WawVariant = 'green' | 'cream' | 'ink';

export interface WawMarkProps {
  /** overall box size in px */
  size?: number;
  /** glyph color when unboxed (ignored when boxed — boxed is always cream-on-green) */
  variant?: WawVariant;
  /** render the app-icon: green rounded square + cream waw (radius = 0.1875×size) */
  boxed?: boolean;
  className?: string;
  /** accessible label; omit to treat as decorative */
  label?: string;
}

const COLOR: Record<WawVariant, string> = {
  green: 'var(--green)',
  cream: 'var(--cream)',
  ink: 'var(--ink)',
};

/**
 * Waw monogram «و» — the lettermark extracted from the wordmark (like Notion's N).
 * Used for app icon, favicon, avatar, watermark, empty states.
 *  - boxed=false → a single green «و» glyph (Thmanyah Display Black)
 *  - boxed=true  → green rounded square (radius 0.1875×size) + cream «و» (≈0.6×size)
 * Never amber. The bespoke vector waw (§2.6) is deferred — Thmanyah base used.
 */
export default function WawMark({
  size = 32,
  variant = 'green',
  boxed = false,
  className,
  label,
}: WawMarkProps) {
  const a11y = label
    ? { role: 'img' as const, 'aria-label': label }
    : { 'aria-hidden': true };

  if (boxed) {
    return (
      <span
        {...a11y}
        className={cn('inline-flex items-center justify-center select-none', className)}
        style={{
          width: size,
          height: size,
          borderRadius: Math.round(size * 0.1875),
          background: 'var(--green)',
          flexShrink: 0,
        }}
      >
        <span
          aria-hidden
          style={{
            color: 'var(--cream)',
            fontFamily: '"Thmanyah Display", "Thmanyah Sans", system-ui, sans-serif',
            fontWeight: 900,
            fontSize: Math.round(size * 0.6),
            lineHeight: 1,
            // optical centering of the waw bowl
            transform: 'translateY(-2%)',
          }}
        >
          {'و'}
        </span>
      </span>
    );
  }

  return (
    <span
      {...a11y}
      className={cn('inline-flex items-center justify-center select-none', className)}
      style={{ width: size, height: size, flexShrink: 0 }}
    >
      <span
        aria-hidden
        style={{
          color: COLOR[variant],
          fontFamily: '"Thmanyah Display", "Thmanyah Sans", system-ui, sans-serif',
          fontWeight: 900,
          fontSize: Math.round(size * 0.92),
          lineHeight: 1,
        }}
      >
        {'و'}
      </span>
    </span>
  );
}
